import React, { useEffect, useState, useCallback } from 'react';
import { authAxios } from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  Sparkles, ChevronDown, ChevronRight, Check, X as XIcon, Edit2,
  RefreshCw, ExternalLink, Loader2,
} from 'lucide-react';

/**
 * Pending AI-drafted events panel. Renders above the main event log
 * and is collapsed by default. Auto-expands when there are pending items.
 *
 * Props:
 *  - API: base URL
 *  - seasonId
 *  - selectedEpisodeId (string or 'all')
 *  - episodes: list of episode objects (for episode picker when 'all')
 *  - scoringRules: for edit dropdown
 *  - aiFeatureEnabled: boolean
 *  - onApproved: () => void  (called after approve to refresh parent event log)
 */
export default function AIPendingDraftsPanel({
  API, seasonId, selectedEpisodeId, episodes, scoringRules,
  aiFeatureEnabled, onApproved,
}) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [running, setRunning] = useState(false);
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const [showRejectAllConfirm, setShowRejectAllConfirm] = useState(false);
  const [editing, setEditing] = useState(null); // draft object

  // The episode this panel currently operates on. When 'all' is selected we
  // require admin to pick one for AI ops.
  const activeEpisodeId = selectedEpisodeId && selectedEpisodeId !== 'all' ? selectedEpisodeId : null;

  const load = useCallback(async () => {
    if (!seasonId || !activeEpisodeId) { setDrafts([]); return; }
    setLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/ai-drafts`, {
        params: { season_id: seasonId, episode_id: activeEpisodeId },
      });
      setDrafts(data || []);
      if ((data || []).length > 0) setExpanded(true);
    } catch (e) {
      // silent on load errors
    } finally {
      setLoading(false);
    }
  }, [API, seasonId, activeEpisodeId]);

  useEffect(() => { load(); }, [load]);

  if (!aiFeatureEnabled) return null;

  const handleApprove = async (id) => {
    try {
      await authAxios.post(`${API}/admin/ai-drafts/${id}/approve`);
      toast.success('Approved');
      await load();
      onApproved && onApproved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    }
  };

  const handleReject = async (id) => {
    try {
      await authAxios.post(`${API}/admin/ai-drafts/${id}/reject`);
      toast.success('Rejected');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    }
  };

  const handleBulkApprove = async () => {
    if (!activeEpisodeId) return;
    try {
      const { data } = await authAxios.post(`${API}/admin/ai-drafts/bulk-approve`, {
        season_id: seasonId, episode_id: activeEpisodeId,
      });
      toast.success(`Approved ${data.approved} events`);
      await load();
      onApproved && onApproved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk approve failed');
    }
  };

  const handleBulkReject = async () => {
    setShowRejectAllConfirm(false);
    if (!activeEpisodeId) return;
    try {
      const { data } = await authAxios.post(`${API}/admin/ai-drafts/bulk-reject`, {
        season_id: seasonId, episode_id: activeEpisodeId,
      });
      toast.success(`Rejected ${data.rejected} events`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk reject failed');
    }
  };

  const handleRerun = async () => {
    setShowRerunConfirm(false);
    if (!activeEpisodeId) return;
    setRunning(true);
    try {
      const { data } = await authAxios.post(`${API}/admin/ai-drafts/rerun`, {
        season_id: seasonId, episode_id: activeEpisodeId,
      });
      if (data.success) {
        toast.success(`AI drafted ${data.drafts_created} events (${data.events_skipped || 0} skipped)`);
      } else {
        toast.error(data.error || 'AI run failed');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rerun failed');
    } finally {
      setRunning(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await authAxios.patch(`${API}/admin/ai-drafts/${editing.id}`, {
        event_name: editing.event_name,
        points: parseInt(editing.points) || 0,
        notes: editing.notes || '',
      });
      toast.success('Updated');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const count = drafts.length;
  const activeEpisode = episodes?.find((e) => e.id === activeEpisodeId);

  return (
    <Card className="mb-4 border-purple-300" data-testid="ai-pending-drafts-panel">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between p-4 hover:bg-purple-50/50 transition-colors"
          data-testid="ai-pending-drafts-toggle"
        >
          <div className="flex items-center gap-3 text-left">
            <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <div>
              <div className="font-semibold text-slate-800 flex items-center gap-2">
                AI Drafted Events
                {activeEpisode && <span className="text-slate-500 text-sm font-normal">— Episode {activeEpisode.number}</span>}
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">BETA</span>
              </div>
              <div className="text-xs text-slate-500">
                {!activeEpisodeId
                  ? 'Select a specific episode (above) to view AI drafts'
                  : count > 0
                    ? `${count} pending review`
                    : 'No pending drafts. Use Rerun below to generate.'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {count > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-purple-600 text-white text-xs font-bold">
                {count}
              </span>
            )}
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </button>

        {expanded && activeEpisodeId && (
          <div className="border-t border-purple-200 p-4 space-y-3 bg-purple-50/30">
            {/* Bulk action bar */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={handleBulkApprove}
                disabled={count === 0 || loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="ai-bulk-approve"
              >
                <Check className="w-4 h-4 mr-1" /> Approve All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRejectAllConfirm(true)}
                disabled={count === 0 || loading}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="ai-bulk-reject"
              >
                <XIcon className="w-4 h-4 mr-1" /> Reject All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRerunConfirm(true)}
                disabled={running}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="ai-rerun"
              >
                {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Rerun for This Episode
              </Button>
              {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>

            {/* Drafts list */}
            {count === 0 && !loading && (
              <div className="text-sm text-slate-600 py-6 text-center">
                No pending AI drafts. Click <strong>Rerun for This Episode</strong> to generate suggestions now.
              </div>
            )}
            {drafts.map((d) => (
              <div
                key={d.id}
                className="bg-white rounded-md border border-purple-200 p-3 flex flex-col sm:flex-row gap-3"
                data-testid={`ai-draft-row-${d.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {d.target_type === 'tribe' ? (
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800">TRIBE</span>
                    ) : null}
                    <span className="font-semibold text-slate-800">
                      {d.display_name || d.contestant_name || d.tribe_name}
                    </span>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-700">{d.event_name}</span>
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800">
                      {d.points > 0 ? `+${d.points}` : d.points} pts{d.target_type === 'tribe' ? ' / member' : ''}
                    </span>
                  </div>
                  {d.ai_reasoning && (
                    <div className="text-xs text-slate-600 italic">{d.ai_reasoning}</div>
                  )}
                  {Array.isArray(d.ai_source_urls) && d.ai_source_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {d.ai_source_urls.map((u, i) => (
                        <a
                          key={i}
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-purple-700 underline hover:text-purple-900"
                        >
                          <ExternalLink className="w-3 h-3" /> source {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex sm:flex-col gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing({ ...d })}
                    data-testid={`ai-draft-edit-${d.id}`}
                  >
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleApprove(d.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`ai-draft-approve-${d.id}`}
                  >
                    <Check className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReject(d.id)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`ai-draft-reject-${d.id}`}
                  >
                    <XIcon className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit AI Draft</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="text-slate-500">
                  {editing.target_type === 'tribe' ? 'Tribe:' : 'Contestant:'}
                </span>{' '}
                <strong>{editing.display_name || editing.contestant_name || editing.tribe_name}</strong>
                {editing.target_type === 'tribe' && (
                  <span className="ml-2 text-xs text-blue-700">(fans out to all members on approve)</span>
                )}
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Event Type</label>
                <Select
                  value={editing.event_name}
                  onValueChange={(v) => {
                    const rule = scoringRules?.find((r) => r.event_name === v);
                    setEditing({ ...editing, event_name: v, points: rule ? rule.points : editing.points });
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {scoringRules?.map((r) => (
                      <SelectItem key={r.id} value={r.event_name}>
                        {r.event_name} ({r.points > 0 ? '+' : ''}{r.points})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Points</label>
                <Input
                  type="number"
                  value={editing.points}
                  onChange={(e) => setEditing({ ...editing, points: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Notes</label>
                <Input
                  value={editing.notes || ''}
                  placeholder="Optional notes"
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rerun confirm */}
      <Dialog open={showRerunConfirm} onOpenChange={setShowRerunConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rerun AI for This Episode?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will <strong>discard the {count} current pending draft{count === 1 ? '' : 's'}</strong> for this
            episode and re-run the AI search now. Approved entries are not affected.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRerunConfirm(false)}>Cancel</Button>
            <Button onClick={handleRerun} className="bg-purple-600 hover:bg-purple-700 text-white">
              <RefreshCw className="w-4 h-4 mr-1" /> Rerun
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject all confirm */}
      <Dialog open={showRejectAllConfirm} onOpenChange={setShowRejectAllConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject All {count} Pending?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            All {count} pending AI drafts for this episode will be marked rejected and removed from this list.
            This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectAllConfirm(false)}>Cancel</Button>
            <Button onClick={handleBulkReject} className="bg-red-600 hover:bg-red-700 text-white">
              Reject All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
